import { useSearchParams, useNavigate } from 'react-router';
import { useTeachers } from '@/hooks/use-admin';
import { useAuthContext } from '@/contexts/auth-context';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { formatDate } from '@/lib/utils';
import { Navigate } from 'react-router';

export default function TeachersPage() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const perPage = 20;

  const { data, isLoading } = useTeachers(page, perPage);

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const updatePage = (p: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(p));
      return next;
    });
  };

  const totalPages = data?.pagination?.total_pages ?? 0;

  const displayName = (firstName: string | null, lastName: string | null, email: string | null) => {
    const parts = [firstName, lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : email ?? '—';
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Students</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Registered</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data?.data || data.data.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No teachers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.data.map((teacher) => (
                    <TableRow
                      key={teacher.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/teachers/${teacher.user_id}`)}
                    >
                      <TableCell className="font-medium">
                        {displayName(teacher.first_name, teacher.last_name, teacher.email)}
                      </TableCell>
                      <TableCell>{teacher.email ?? '—'}</TableCell>
                      <TableCell>{teacher.student_count}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{teacher.tier}</Badge>
                      </TableCell>
                      <TableCell>{formatDate(teacher.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => updatePage(Math.max(1, page - 1))}
                    aria-disabled={page <= 1}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {(() => {
                  const windowSize = 5;
                  let start = Math.max(1, page - Math.floor(windowSize / 2));
                  const end = Math.min(totalPages, start + windowSize - 1);
                  start = Math.max(1, end - windowSize + 1);
                  return Array.from({ length: end - start + 1 }, (_, i) => {
                    const p = start + i;
                    return (
                      <PaginationItem key={p}>
                        <PaginationLink
                          isActive={p === page}
                          onClick={() => updatePage(p)}
                          className="cursor-pointer"
                        >
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  });
                })()}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => updatePage(Math.min(totalPages, page + 1))}
                    aria-disabled={page >= totalPages}
                    className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
